import { useAuthStore } from 'state/store/auth/useAuthStore';
import { getRefreshToken } from 'features/auth/services/auth-service';
import { useConfigStore } from '../state/store/config/useConfigStore';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';

interface Https {
  get<T>(url: string, blocksKey: string, headers?: HeadersInit): Promise<T>;

  post<T>(
    url: string,
    blocksKey: string,
    body: BodyInit,
    headers?: HeadersInit,
    noContentType?: boolean
  ): Promise<T>;

  put<T>(url: string, blocksKey: string, body: BodyInit, headers?: HeadersInit): Promise<T>;

  delete<T>(url: string, blocksKey: string, headers?: HeadersInit): Promise<T>;

  request<T>(
    url: string,
    blocksKey: string,
    options: RequestOptions,
    noContentType?: boolean
  ): Promise<T>;

  createHeaders(blocksKey: string, headers: any, noContentType?: boolean): Headers;

  handleAuthError<T>(
    url: string,
    method: string,
    headers: any,
    body: any,
    noContentType?: boolean
  ): Promise<T>;
}

interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: HeadersInit;
  body?: BodyInit;
}

// const prepareUrl = (url: string): string => {
//   if (url.startsWith('https://dev-api.eaglegpt.ai/taskforce')) {
//     return url.replace(
//       'https://dev-api.eaglegpt.ai/taskforce',
//       'https://taskforce.cloudwhimsy.com/taskforce'
//     );  
//   }
//   return url;
// };

// https://taskforce.cloudwhimsy.com/taskforce/api/collaboration/conversations
export class HttpError extends Error {
  status: number;
  error: Record<string, unknown>;

  constructor(status: number, error: Record<string, unknown>) {
    const errorMessage = typeof error.message === 'string' ? error.message : JSON.stringify(error);

    super(errorMessage);
    this.status = status;
    this.error = error;
  }
}



export const clients: Https = {
  async get<T>(url: string, blocksKey: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, blocksKey, { method: 'GET', headers });
  },
  

  async post<T>(
    url: string,
    blocksKey: string,
    body: BodyInit,
    headers: HeadersInit = {},
    noContentType = false
  ): Promise<T> {
    return this.request<T>(
      url,
      blocksKey,
      {
        method: 'POST',
        headers,
        body,
      },
      noContentType
    );
  },

  async put<T>(
    url: string,
    blocksKey: string,
    body: BodyInit,
    headers: HeadersInit = {}
  ): Promise<T> {
    return this.request<T>(url, blocksKey, { method: 'PUT', headers, body });
  },

  
  async delete<T>(url: string, blocksKey: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, blocksKey, { method: 'DELETE', headers });
  },

  async request<T>(
    url: string,
    blocksKey: string,
    { method, headers = {}, body }: RequestOptions,
    noContentType = false
  ): Promise<T> {
    const requestHeaders = this.createHeaders(blocksKey, headers, noContentType);
    // @todo need to remove this before merging into the dev
    // url = prepareUrl(url); 

    const config: RequestInit = {
      method,
      credentials: 'include',
      headers: requestHeaders,
    };

    if (body) {
      config.body = body;
    }

    const response = await fetch(url, config);

    if (response.ok) {
      if (response.headers.get('Content-Type')?.includes('audio/')) {
        return response as T;
      }
      if (response.headers.get('Content-Type')?.includes('text/event-stream')) {
        return response as T;
      }
      const responseText = await response.text();
      const finalResponseText = responseText.trim() === '' ? '{}' : responseText;
      try {
        return JSON.parse(finalResponseText) as T;
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        throw new HttpError(response.status, {
          error: 'Invalid JSON response',
          rawResponse: finalResponseText,
        });
      }
    }

    if (response.status === 401) {
      trackEvent(EVENTS.ERROR("Unauthorized", {
        endpoint: url,
        method,
        responseStatus: response.status
      }));
      return this.handleAuthError<T>(url, method, headers, body, noContentType);
    }

    const err = await response.json();
    trackEvent(EVENTS.ERROR(err, {
      endpoint: url,
      method,
      responseStatus: response.status
    }));
    throw new HttpError(response.status, err);
  },

  createHeaders(blocksKey: string, headers: any, noContentType = false): Headers {
    const authToken = useAuthStore.getState().accessToken;

    const baseHeaders = {
      'Content-Type': 'application/json',
      'x-blocks-key': blocksKey,
      ...(authToken && { Authorization: `bearer ${authToken}` }),
    };

    const headerEntries =
      headers instanceof Headers ? Object.fromEntries(headers.entries()) : headers;

    const _headers = new Headers({
      ...baseHeaders,
      ...headerEntries,
    });

    if (noContentType) _headers.delete('Content-Type');
    return _headers;
  },

  async handleAuthError<T>(
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    headers: any,
    body: any,
    noContentType = false
  ): Promise<T> {
    const logout = useAuthStore.getState().logout;

    const config = useConfigStore.getState().configurationSettings;
    if (!config) {
      trackEvent(EVENTS.ERROR("Configuration settings not found", {
        endpoint: url,
        method,
        responseStatus: 400
      }));
      logout();
      window.location.href = '/login';
      return Promise.reject(new HttpError(400, { error: 'Configuration settings not found' }));
    }

    const authStore = useAuthStore.getState();

    if (!authStore.refreshToken) {
      trackEvent(EVENTS.ERROR("Invalid refresh token", {
        endpoint: url,
        method,
        responseStatus: 400
      }));
      logout();
      window.location.href = '/login';
    }

    const refreshTokenRes = await getRefreshToken(config);

    if (refreshTokenRes.error === 'invalid_refresh_token') {
      trackEvent(EVENTS.ERROR("Invalid refresh token", {
        endpoint: url,
        method,
        responseStatus: 400
      }));
      logout();
      window.location.href = '/login';
    }

    authStore.setAccessToken(refreshTokenRes.access_token);
    return this.request<T>(
      url,
      config.blocksKey,
      {
        method,
        headers,
        body,
      },
      noContentType
    );
  },
};
